import type { Conversation, UserId, UserRef } from '@cyphex/types';

export interface ConversationView {
  title: string;
  subtitle: string;
  /** The other person in a DM — drives the avatar and presence dot. */
  counterpart: UserRef | null;
  isGroup: boolean;
  memberNames: string[];
}

/** Derives everything the list row and header render from a conversation. */
export function viewOf(
  conversation: Conversation | null,
  meId: UserId | null,
  /**
   * The signed-in user, for the one conversation that is about them.
   *
   * It has to be passed in: `GET /conversations` returns `members: []` on the
   * list (the payload is deliberately thin), so the self chat cannot name itself
   * from its own membership — the first version of this tried, and silently fell
   * back to the stored title on every row in the sidebar.
   */
  viewer: UserRef | null = null,
): ConversationView {
  if (!conversation) {
    return { title: '', subtitle: '', counterpart: null, isGroup: false, memberNames: [] };
  }

  /**
   * YOUR OWN CHAT IS NAMED AFTER YOU, the way Teams names it: "Vamshi Uppala
   * (You)" rather than a product noun like "Notes to self". It reads as a
   * person in a list of people instead of as a feature, and the "(You)" is what
   * stops it being mistaken for a DM with someone of the same name.
   *
   * The single member IS you, so the name, avatar and presence all come from
   * that row — no extra plumbing, and no separate identity to keep in sync.
   * Handing it back as `counterpart` is what makes the list row and the thread
   * header show YOUR face and YOUR presence; with null they fell back to blank
   * initials and a hard-coded "Offline" on your own conversation.
   */
  if (conversation.isSelfChat) {
    const me =
      viewer ??
      conversation.members.find((member) => member.user.id === meId)?.user ??
      conversation.members[0]?.user ??
      null;
    return {
      title: me === null ? (conversation.title ?? 'Notes to self') : `${me.displayName} (You)`,
      subtitle: 'Only you',
      counterpart: me,
      isGroup: false,
      memberNames: [],
    };
  }

  const others = conversation.members.filter((member) => member.user.id !== meId);
  const isGroup = conversation.kind === 'group' || others.length > 1;
  const counterpart = others[0]?.user ?? null;
  const memberNames = others.map((member) => member.user.displayName);

  if (conversation.title) {
    return {
      title: conversation.title,
      subtitle: `${conversation.memberCount} members`,
      counterpart,
      isGroup,
      memberNames,
    };
  }

  if (isGroup) {
    return {
      title: memberNames.slice(0, 3).join(', ') || 'Group chat',
      subtitle: `${conversation.memberCount} members`,
      counterpart,
      isGroup: true,
      memberNames,
    };
  }

  return {
    title: counterpart?.displayName ?? 'Conversation',
    subtitle: counterpart?.jobTitle ?? '',
    counterpart,
    isGroup: false,
    memberNames,
  };
}

/**
 * An unsent draft, trimmed for the list row. `null` when there is nothing
 * pending, which is what lets the row fall back to the last message.
 */
export function draftLine(conversation: Conversation): string | null {
  const text = conversation.self.draft?.text?.trim();
  return text ? text.slice(0, 120) : null;
}

/** `You: shipped it` — the second line in the list row. */
export function previewLine(conversation: Conversation, meId: UserId | null): string {
  const last = conversation.lastMessage;
  if (!last) return 'No messages yet';
  /* The preview is a denormalised COPY of the message, so a bad writer
     upstream can leave it without its text — and template interpolation
     then renders the literal string "undefined" into the sidebar, which is
     exactly what happened. Treat a preview with no usable text as no
     preview at all. */
  const text = typeof last.text === 'string' ? last.text.trim() : '';
  if (text.length === 0) return 'No messages yet';
  if (last.senderId === meId) return `You: ${text}`;
  const sender = conversation.members.find((member) => member.user.id === last.senderId);
  const prefix = conversation.kind === 'dm' ? '' : `${sender?.user.displayName.split(' ')[0] ?? ''}: `;
  return `${prefix}${text}`;
}
